import { Injectable, Injector, signal, untracked, inject, effect } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseProjectService, StorageService, OfflineModeService, ConnectionService, PresenceService, ProjectWorkflowState, WorkflowDocument, isFrozen, isProjectFinalized, effectiveStatus, belongsToOtherProject, canEditPoint, canOverwriteOnRestore, isOwnPoint, takeoverPreviousProject, WorkflowRoleFlags, ProjectInvite, acceptInviteInto, ConfirmService,
	ConsoleErrorService,
} from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { RoleDefinition } from './role-model';
import { cloneDeep } from 'lodash';
import { MapDefinition, Regions, LuzulaMeta } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';
import { praesenzApp } from './presence/praesenz-app';
import { ZeichnungenService } from './zeichnungen.service';
import { BereicheService } from './bereiche.service';
import { LayoutsService } from './layouts.service';
import { GeomFeaturesService } from './geom-features.service';
import { BPWEB_DESIGN_DOCS } from './core/bpweb-design-docs';

export interface ProjectMember {
	id: string;
	username: string;
	firstName?: string;
	lastName?: string;
}

/**
 * Berechtigungen für Punkte und Bereiche im Projekt.
 * Anlegen und Importieren implizieren Ändern.
 */
export interface ProjectPermissions {
	anlegen: boolean;         // Punkte anlegen
	loeschen: boolean;        // Punkte löschen
	importieren: boolean;     // Punkte importieren
	aendern: boolean;         // Punkte ändern
	zuordnen: boolean;        // Punkte dem Projekt zuordnen
	bereicheAnlegen: boolean; // Bereiche anlegen (Rechteck, Polygon, GeoJSON-Import)
	bereicheLoeschen: boolean; // Bereiche löschen
}

/**
 * Metadaten für Projekt-Dateianhänge.
 * Die eigentlichen Binärdaten werden als PouchDB _attachments gespeichert.
 */
export interface ProjectAttachmentMeta {
	name: string;           // Original-Dateiname
	contentType: string;    // MIME-Type (image/jpeg, application/pdf, etc.)
	size: number;           // Dateigröße in Bytes
	uploadedAt: string;     // ISO-Datum
}

export interface Project {
	name: string;
	description: string;
	mapDefinition?: MapDefinition;
	regions?: Regions;

	owner?: ProjectMember;
	bearbeiter?: ProjectMember[];
	kontrolleure?: ProjectMember[];

	/**
	 * @deprecated Projektweite Berechtigungs-Flags abgeschafft (2026-05-25). Werden
	 * in bpweb nicht mehr gelesen (Rollen + Workflow-Status regeln alles); Feld
	 * bleibt nur, damit Altprojekte ohne Migration ladbar sind. UI ausgeblendet
	 * via `permissionsVisible: false`.
	 */
	permissions?: ProjectPermissions;

	lastOpenedSection?: string;

	// Liste der hinzugefügten Geodatendateien (Namen als Referenz)
	geodataFiles?: string[];

	// Metadaten für Dateianhänge (Binärdaten in _attachments)
	attachmentsMeta?: ProjectAttachmentMeta[];

	/**
	 * Projekt-Workflow (Status: bearbeitung/kontrolle/abgeschlossen/abgelehnt).
	 * Wird beim Anlegen mit `bearbeitung` initialisiert. Wenn Projekt-Status
	 * + Punkt-Status beide `abgeschlossen` sind, ist der Punkt frozen.
	 */
	workflow?: ProjectWorkflowState;

	/**
	 * Offene Einladungen (Phase K, Variante B). Ein Bearbeiter-Owner lädt
	 * Mitglieder per E-Mail ein; der Eingeladene nimmt über die Projektliste an
	 * (`acceptInvite`). Discovery beim Eingeladenen läuft über die `by_invite`-View.
	 */
	pendingInvites?: ProjectInvite[];

	/**
	 * Laufzeit-Markierung (NICHT persistiert): Das Projekt erscheint in der Liste
	 * nur, weil eine offene Einladung an den aktuellen Nutzer existiert — er ist
	 * noch kein Mitglied. Setzt `refreshProjectList` beim Merge der by_invite-Treffer.
	 */
	invitedOnly?: boolean;

	/**
	 * Archiviert: in der Projektauswahl standardmäßig ausgeblendet (über den
	 * „Archiv anzeigen"-Filter sichtbar). Rein organisatorisch — ändert KEINE
	 * Rechte und friert nichts ein; das Projekt bleibt voll funktionsfähig.
	 * Setzen/Zurücknehmen nur Owner/Manager/Entwickler (`setProjectArchived`).
	 */
	archived?: boolean;

	/**
	 * Gruppenbasierte Datensicht (feature/user-groups, Modell B): die Gruppe(n), denen das
	 * Projekt zugeordnet ist. Muss in der Projektliste mitgetragen werden (Views emittieren es,
	 * `zuordneGruppe` persistiert es ins Doc), sonst zeigt die Auswahl nach dem Refresh wieder
	 * „nicht zugeordnet". Server-seitig erzwingt der DB-Gateway die Isolation nach diesem Feld.
	 */
	groups?: string[];

	/**
	 * Kartierverfahren des Projekts: **fehlt = SEA 95**, `'ka'` = Bodenkundliche
	 * Kartieranleitung (mit `kaAuflage`). Wird allein BEIM ANLEGEN gesetzt (Abschnitt
	 * „Kartierverfahren" im Fenster „Neues Projekt") und danach nirgends geändert.
	 * Es bestimmt, welchen Vermerk ein NEUER Punkt bekommt; gelesen wird es aus den
	 * Projektwerten (`getProjectValue('verfahren')`), weil die Projektliste online
	 * aus einer schlanken Sicht ohne dieses Feld entsteht. Reine Datenfelder, keine
	 * Berechtigung hängt daran. Siehe bohrpunkte/verfahren/verfahren.ts.
	 */
	verfahren?: 'sea95' | 'ka';
	kaAuflage?: 5 | 6;

	/**
	 * Spur der Bibliothek: wer das Projekt angelegt und wer es zuletzt
	 * bearbeitet hat (Name, Beschreibung, Mitglieder). Siehe `LuzulaMeta`.
	 */
	luzula?: LuzulaMeta;

	/**
	 * Wann zuletzt jemand in diesem Projekt etwas geschrieben hat
	 * (Millisekunden). Gesetzt nach dem Speichern von Punkten, Zeichnungen und
	 * Editor-Flächen, gedrosselt. Treibt die Spalte „Letzte Änderung" in der
	 * Projektauswahl - die bis zum 22.09.2026 nur Statuswechsel kannte und
	 * deshalb praktisch feststand. Regel: `core/project/zuletzt-gearbeitet.ts`.
	 */
	zuletztGearbeitet?: number;
}

export interface ProjectDocument extends Project {
	_id: string;
	_rev?: string;
}

@Injectable({
	providedIn: 'root'
})
export class ProjectService extends BaseProjectService<ProjectDocument> {
	private dbConfig = inject(DatabaseConfigService);
	private zeichnungen = inject(ZeichnungenService);
	private bereiche = inject(BereicheService);
	private layouts = inject(LayoutsService);
	private bpInjector = inject(Injector);

	// ========== Abstract Service Getter Implementierungen ==========

	protected override get storage() { return this._storage; }
	protected override get oms() { return this._oms; }
	protected override get auth() { return this._auth; }
	protected override get msg() { return this._msg; }
	protected override get connectionMonitor(): Observable<boolean> { return this.cs.monitor(); }
	protected override get authChanged(): Observable<void> { return this._auth.authChanged; }

	// ========== BasePouchDbService Implementierungen ==========

	get remoteDbName(): string {
		return this.dbConfig.projekteDbName;
	}

	protected get userOfflineModeSignal() {
		return this._oms.userOfflineMode;
	}

	protected get networkOnlineSignal() {
		// ProjectService nutzt ConnectionService, nicht OfflineModeService.networkOnline
		// Return constant signal - Netzwerkänderungen werden über connectionMonitor behandelt
		return signal<boolean>(true);
	}

	// ========== Hooks ==========

	protected override async initDesignDocs(db: PouchDB.Database): Promise<void> {
		// Wird von base-pouchdb nur mit gueltigem Token aufgerufen (authReady-Gate),
		// laeuft also nie im Pre-Token-Fenster. Das Gateway hebt `_design/*`-Writes
		// auf Admin-Creds (der Writer-Service-Account ist nur Member und duerfte
		// sonst keine Design-Docs schreiben). Legacy nutzt die Admin-Creds aus der
		// URL. Definition: BPWEB_DESIGN_DOCS (Single Source of Truth).
		const views = BPWEB_DESIGN_DOCS[0];
		try {
			const existing = await db.get('_design/app_views');
			const existingWithoutRev = cloneDeep(existing);
			delete (existingWithoutRev as any)._rev;
			if (JSON.stringify(existingWithoutRev) !== JSON.stringify(views)) {
				await db.put({
					...views,
					_rev: existing._rev
				});
			}
		} catch (err: any) {
			if (err.status === 404) {
				await db.put(views);
			}
		}
	}

	protected override onProjectSaved(doc: any): void {
		// Permissions könnten sich geändert haben → authChanged triggern
		this._auth.authChanged.emit();
	}

	// ========== Constructor ==========

	private _msg = inject(ToastrService);
	private cs = inject(ConnectionService);
	private _storage = inject(StorageService);
	private _oms = inject(OfflineModeService);
	private _auth = inject(AuthService);
	private _presence = inject(PresenceService);
	private _confirmService = inject(ConfirmService);
	/** Trichter fuer Fehler, die wir wissen wollen, ohne den Ablauf abzubrechen. */
	private ces = inject(ConsoleErrorService);

	/** Sperre gegen Rekursion in `ensureBearbeiterMember` (loadProject → save → loadProject). */
	private bearbeiterMigrationRunning = false;


	constructor() {
		super();
		this.initializeBaseProjectService();

		// loggedIn statt isAuthenticated: BehaviorSubject `isAuthenticated`
		// wird auch aus dem localStorage-Cache (retrieveAuthInfo) auf true
		// gesetzt, BEVOR Keycloak intern fertig initialisiert ist — der
		// Bearer-Interceptor catched dann beim conditionallyUpdateToken
		// und feuert ohne Token (= 401). loggedIn.emit() erfolgt erst,
		// nachdem authImpl.isAuthenticated$ echten Auth gemeldet hat.
		//
		// Plus User-Wechsel-Erkennung (User-Befund 2026-06-01, Bug 2): Wenn sich
		// die `currentUserId` zwischen zwei `loggedIn`-Events ändert (anderer User
		// hat sich angemeldet), wird das aktive Projekt entladen — sonst würde
		// der Auto-Restore aus dem StorageService einem anderen User Projekt-
		// State zeigen, zu dem er keinen Zugriff hat. Session-Token-Refresh
		// (gleiche UserId) bleibt davon unberührt; Logout selbst räumt schon
		// im auth-button.component auf.
		let lastUserIdSeen = '';
		this._auth.loggedIn.subscribe(async () => {
			const userId = this._auth.currentUserId;
			if (lastUserIdSeen && lastUserIdSeen !== userId) {
				try { await this.unloadCurrentProject(); }
				catch (err) { console.warn('[ProjectService] User-Wechsel-Unload fehlgeschlagen:', err); }
			}
			lastUserIdSeen = userId;
		});

		// Praesenz: solange ein Projekt aktiv ist UND Auth bereit ist,
		// bpweb-Sitzung registrieren. Das Backend-TTL haelt die Eintraege
		// 10 min; der Service heartbeated alle 2 min. Beim Projekt-Wechsel
		// wird die Sitzung automatisch umgemeldet, beim Verlassen
		// (project()='') gestoppt.
		// Die API sucht das Projekt in der DB der passenden App (boden, bpwebmv,
		// im Dev-Modus mit -dev) - siehe praesenzApp. Kennt sie die Variante
		// nicht (lfmv), meldet die App keine Praesenz statt alle 2 min ein 404.
		const app = praesenzApp(this.dbConfig.namensraum, this.dbConfig.isDevelopment);
		effect(() => {
			if (!this._auth.authReady()) return;
			const id = this.project();
			if (id && app) this._presence.start(id, 'bpweb', app);
			else           this._presence.stop();
		});
	}

	// ========== App-spezifische abstrakte Methoden ==========

	/**
	 * Aktualisiert die Projektliste basierend auf dem aktuellen Modus.
	 * Manager sehen alle Projekte, andere Benutzer nur Projekte,
	 * für die sie Eigentümer, Kartierer oder Kontrolleur sind.
	 */
	protected async refreshProjectList(): Promise<void> {
		if (this._oms.userOfflineMode() && this.localDb) {
			// Im Offline-Modus: Nur lokale Projekte anzeigen
			const docs = await this.localDb.allDocs({ include_docs: true });
			const projects = docs.rows
				.filter(row => !row.id.startsWith('_design/'))
				.map(row => ({
					_id: row.id,
					name: (row.doc as any)?.name ?? row.id,
					description: (row.doc as any)?.description,
					owner: (row.doc as any)?.owner,
					bearbeiter: (row.doc as any)?.bearbeiter,
					kontrolleure: (row.doc as any)?.kontrolleure,
					archived: (row.doc as any)?.archived,
					groups: (row.doc as any)?.groups,
					luzula: (row.doc as any)?.luzula,
					zuletztGearbeitet: (row.doc as any)?.zuletztGearbeitet,
					workflow: (row.doc as any)?.workflow
				} as ProjectDocument));
			this.projectList.set(projects);
		} else if (this.remoteDb) {
			if (this._auth.canIbe('manager') || this._auth.canIbe('entwickler')) {
				// Manager/Entwickler sehen alle Projekte (§4.1 P2). Bearbeiter/
				// Kontrolleur sehen nur die eigene Mitgliedschaft (by_member, unten).
				const docs = await this.remoteDb.query<Project>('app_views/by_id', { include_docs: false });
				this.projectList.set(docs.rows.map((row: any) => ({
					_id: row.id,
					name: row.value.name,
					description: row.value.description,
					owner: row.value.owner,
					bearbeiter: row.value.bearbeiter,
					kontrolleure: row.value.kontrolleure,
					archived: row.value.archived,
					groups: row.value.groups,
					luzula: row.value.luzula,
					zuletztGearbeitet: row.value.zuletztGearbeitet,
					workflow: row.value.workflow
				})));
			} else if (this._auth.isDatennutzer()) {
				// User-Entscheidung 2026-06-02 (§4 P2): Reine Datennutzer haben
				// KEINEN Projekt-Zugriff. Projektliste bleibt leer, das Panel
				// wird in `ProjectManagerComponent` ohnehin verborgen
				// (`canSeeProjects`). Defense-in-depth: hier ebenfalls leer
				// setzen, falls jemand das Modal trotzdem programmatisch öffnet.
				this.projectList.set([]);
			} else {
				// Andere Benutzer sehen nur eigene Projekte
				const userId = this._auth.currentUserId;
				if (!userId) {
					this.projectList.set([]);
					return;
				}

				const docs = await this.remoteDb.query<Project>('app_views/by_member', {
					key: userId,
					include_docs: false
				});

				// Duplikate entfernen
				const uniqueProjects = new Map<string, ProjectDocument>();
				docs.rows.forEach((row: any) => {
					if (!uniqueProjects.has(row.value._id)) {
						uniqueProjects.set(row.value._id, {
							_id: row.value._id,
							name: row.value.name,
							description: row.value.description,
							owner: row.value.owner,
							bearbeiter: row.value.bearbeiter,
							kontrolleure: row.value.kontrolleure,
							archived: row.value.archived,
							groups: row.value.groups,
							luzula: row.value.luzula,
							zuletztGearbeitet: row.value.zuletztGearbeitet,
							workflow: row.value.workflow
						} as ProjectDocument);
					}
				});

				// Phase K: Zusätzlich Projekte einblenden, zu denen der Nutzer per
				// E-Mail EINGELADEN ist, aber (noch) kein Mitglied. Discovery über die
				// by_invite-View (Key = E-Mail, lowercase). Diese erscheinen mit
				// `invitedOnly: true` + den `pendingInvites` für Badge/Annehmen.
				await this.mergeInvitedProjects(uniqueProjects);

				this.projectList.set([...uniqueProjects.values()]);
			}
		}
	}

	/**
	 * Fügt der Projektliste die per E-Mail eingeladenen (Nicht-Mitglieds-)Projekte
	 * hinzu. Bereits als Mitglied sichtbare Projekte werden NICHT überschrieben
	 * (kein „Einladung"-Badge, wenn man ohnehin schon dabei ist).
	 */
	private async mergeInvitedProjects(into: Map<string, ProjectDocument>): Promise<void> {
		// Discovery über ALLE eigenen Identifikatoren — E-Mail, Login-Benutzername
		// (preferred_username) UND Anzeigename. So findet der Eingeladene das Projekt
		// unabhängig davon, welchen davon der Owner getippt hat („alles trifft").
		const keys = [
			this._auth.currentUserEmail,
			this._auth.currentUsername,
			this._auth.currentUser
		]
			.map(v => v?.trim().toLowerCase())
			.filter((v): v is string => !!v);
		if (!keys.length || !this.remoteDb) return;
		try {
			const invited = await this.remoteDb.query<Project>('app_views/by_invite', {
				keys: [...new Set(keys)],
				include_docs: false
			});
			invited.rows.forEach((row: any) => {
				const id = row.value._id;
				if (into.has(id)) return; // schon Mitglied → kein Einladungs-Eintrag
				into.set(id, {
					_id: id,
					name: row.value.name,
					description: row.value.description,
					owner: row.value.owner,
					groups: row.value.groups,
					pendingInvites: row.value.pendingInvites,
					invitedOnly: true
				} as ProjectDocument);
			});
		} catch (err) {
			// Fehlende/alte by_invite-View (noch nicht gebaut) darf die Liste nicht sprengen.
			console.warn('[Project] by_invite-Discovery fehlgeschlagen:', err);
		}
	}

	/**
	 * Bildet eine Einladungs-Rolle auf das konkrete Projekt-Feld ab (Phase K).
	 *
	 * Robust gegen die drei Namensräume, in denen die Rolle vorkommen kann:
	 *  - App-Rolle (`bearbeiter`/`kontrolleur`),
	 *  - Keycloak-Rolle (`kartierer` — der UI-Dropdown speichert den aus `authRole`
	 *    abgeleiteten Wert; der Realm ist NICHT migriert, daher `kartierer`),
	 *  - Projekt-Feld (`bearbeiter`/`kontrolleure`).
	 * Ohne `kartierer`-Zweig wäre eine Bearbeiter-Einladung nicht annehmbar (Bug).
	 */
	private fieldForInviteRole(role: string): string | undefined {
		if (role === 'bearbeiter' || role === 'kartierer') return 'bearbeiter';
		if (role === 'kontrolleur' || role === 'kontrolleure') return 'kontrolleure';
		return undefined;
	}

	/**
	 * Annahme einer Einladung (Phase K, Variante B). Trägt den aktuellen Nutzer
	 * selbst in die eingeladene Rolle ein — ABER nur, wenn am Projekt-Doc eine zur
	 * eigenen E-Mail passende Einladung vorliegt (Gate in `acceptInviteInto`). Ohne
	 * Treffer wird NICHT gespeichert und `false` zurückgegeben.
	 */
	async acceptInvite(projectId: string): Promise<boolean> {
		if (!projectId) return false;
		// Annahme matcht über ALLE eigenen Identifikatoren (E-Mail / Login-Name /
		// Anzeigename) — passend zu „alles trifft" im Einladungs-Dialog.
		const identity = {
			email: this._auth.currentUserEmail,
			username: this._auth.currentUsername,
			displayName: this._auth.currentUser
		};
		if (!identity.email && !identity.username && !identity.displayName) return false;
		const doc = await this.getOne(projectId) as ProjectDocument | undefined;
		if (!doc) return false;

		const member: ProjectMember = { id: this._auth.currentUserId, username: this._auth.currentUser };
		const result = acceptInviteInto(doc, {
			identity,
			member,
			fieldForRole: r => this.fieldForInviteRole(r)
		});
		if (!result.accepted) return false;

		await this.save(result.doc);
		await this.refreshProjectList();
		return true;
	}

	/**
	 * Cascade-Delete: beim Löschen eines Projekts auch dessen Zeichnungs-Docs
	 * (bpweb-zeichnungen) entfernen — sonst bleiben verwaiste Feature/Meta-Docs
	 * liegen und tauchen bei einem Restore desselben Projekts (gleiche _id) wieder
	 * auf.
	 *
	 * **Erst das Projekt, dann seine Daten - und nur, wenn es auf dem Server
	 * wirklich weg ist** (28.09.2026). Vorher gingen die Zeichnungen zuerst. Das
	 * Löschen des Projekt-Dokuments meldet aber auch dann Erfolg, wenn der Server
	 * es abweist, und Löschmarken ohne Gruppe lässt das Gateway durch: Das Projekt
	 * blieb stehen, seine Zeichnungen waren weg. Jetzt die sichere Richtung: Scheitert
	 * etwas, bleiben eher Reste liegen, als dass Zeichnungen verloren gehen.
	 */
	override async deleteProject(projectId: string): Promise<boolean> {
		if (!projectId) return super.deleteProject(projectId);
		if (!(await super.deleteProject(projectId))) return false;
		if (!(await this.projektAufDemServerWeg(projectId))) {
			console.warn(`[ProjectService] Projekt ${projectId} steht auf dem Server noch - Zeichnungen, Bereiche und Druck-Layouts bleiben.`);
			return false;
		}
		try {
			await this.zeichnungen.removeProject(projectId);
		} catch (e) {
			// Gemeldet, obwohl bewusst weitergemacht wird: Liegengebliebene Reste
			// sind die sichere Richtung, aber sie sind nicht folgenlos — ein Restore
			// desselben Projekts (gleiche _id) belebt sie wieder. Wer das erst vom
			// Nutzer erfaehrt, sucht am falschen Ende.
			console.warn('[ProjectService] Cascade-Delete der Zeichnungen fehlgeschlagen:', e);
			this.ces.melde('[Projekt loeschen] Zeichnungen blieben liegen', e);
		}
		// P5 (PLAN-bereiche-db.md): auch die Bereich-Docs (bpweb-bereiche)
		// entfernen — sonst belebt ein Restore desselben Projekts (gleiche _id)
		// die verwaisten Bereiche wieder.
		try {
			await this.bereiche.removeProject(projectId);
		} catch (e) {
			console.warn('[ProjectService] Cascade-Delete der Bereiche fehlgeschlagen:', e);
			this.ces.melde('[Projekt loeschen] Bereiche blieben liegen', e);
		}
		// Auch die Druck-Layout-Docs (bpweb-layouts) entfernen — sonst belebt ein
		// Restore desselben Projekts (gleiche _id) verwaiste Layouts wieder.
		try {
			await this.layouts.removeProject(projectId);
		} catch (e) {
			console.warn('[ProjectService] Cascade-Delete der Druck-Layouts fehlgeschlagen:', e);
			this.ces.melde('[Projekt loeschen] Druck-Layouts blieben liegen', e);
		}
		// Und die Flaechen des Karten-Editors (`g::` in `<base>-geom`, V3 30.09.2026):
		// Sie gingen nie mit, blieben auf dem Server und kamen mit einem Projekt
		// derselben Kennung (Wiederherstellen) zurueck.
		try {
			await this.geometrienFuerLoeschen().removeProject(projectId);
		} catch (e) {
			console.warn('[ProjectService] Cascade-Delete der Geometrien fehlgeschlagen:', e);
			this.ces.melde('[Projekt loeschen] Geometrien blieben liegen', e);
		}
		return true;
	}

	/**
	 * Der Geometrie-Dienst, erst beim Loeschen geholt: `GeomFeaturesService` holt sich
	 * seinerseits diesen Dienst als Feld — als Feld hier injiziert, gaebe das einen Kreis.
	 */
	protected geometrienFuerLoeschen(): { removeProject(projectId: string): Promise<void> } {
		return this.bpInjector.get(GeomFeaturesService);
	}

	/**
	 * Ist das Projekt-Dokument auf dem Server wirklich weg? Ohne Server-Verbindung
	 * (offline) gilt die lokale Löschung - wie bisher. Kann nicht nachgesehen
	 * werden (Netzfehler), gilt es als NICHT weg: Dann bleiben die Zeichnungen.
	 */
	private async projektAufDemServerWeg(projectId: string): Promise<boolean> {
		if (!this.remoteDb) return true;
		try {
			await this.remoteDb.get(projectId);
			return false;
		} catch (e: any) {
			return e?.status === 404;
		}
	}

	/**
	 * Generiert eine Projekt-ID nach dem Schema YYYYMMDD_Projektname_GUID
	 */
	protected override generateProjectId(projectName: string): string {
		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		const dateStr = `${year}${month}${day}`;

		const sanitizedName = projectName
			.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
			.replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
			.replace(/ß/g, 'ss')
			.replace(/[^a-zA-Z0-9]/g, '_')
			.replace(/_+/g, '_')
			.replace(/^_|_$/g, '');

		const guid = crypto.randomUUID().split('-')[0];

		return `${dateStr}_${sanitizedName}_${guid}`;
	}

	// ========== Berechtigungsprüfungen ==========

	/**
	 * Frozen-Lock: abgeschlossener Punkt, der NICHT zum aktiven Projekt gehoert.
	 * - Gleiche projectId wie das aktive Projekt → nicht frozen (Kontrolleur kann
	 *   z.B. den Punkt via Workflow-Transition zurueckholen).
	 * - Fremde projectId oder keine projectId (Legacy) und Punkt-Status
	 *   `abgeschlossen` → frozen, kein Edit moeglich.
	 */
	isPointFrozen(point: WorkflowDocument | null | undefined): boolean {
		if (!point) return false;
		const activeProjectId = untracked(() => this.project());
		return isFrozen(point, activeProjectId);
	}

	/**
	 * Harter Projekt-Lock (Lebenszyklus 2026-05-27): Ein explizit abgeschlossenes
	 * Projekt (`workflow.finalizedAt` gesetzt) friert ALLES ein — Punkte,
	 * Zeichnungen, Layer, Bereiche — für ALLE Rollen inkl. Entwickler. Nur das
	 * „Projekt wieder öffnen" (Manager/Entwickler) hebt den Lock auf. Abgrenzung:
	 * „Bereit zum Abschluss" (abgeleitet `abgeschlossen`, aber NICHT finalisiert)
	 * friert nichts ein.
	 */
	isCurrentProjectFinalized(): boolean {
		return isProjectFinalized(untracked(() => this.currentProjectDoc()));
	}

	/**
	 * Punkt-spezifische Schreibberechtigung: kombiniert die generelle
	 * canI('write','points')-Logik (Projekt-Permission `aendern` + Rolle) mit dem
	 * Workflow-Status-/Rollen-/Ownership-Gate (`canEditPoint`).
	 *
	 * Test-Fund #1 / Phase J: Das Workflow-Gate ist **status-agnostisch** — es
	 * sperrt einen Bearbeiter automatisch auf `kontrolle`/`in_pruefung`/
	 * `abgeschlossen` (nur eigene `bearbeitung` editierbar) und einen Kontrolleur
	 * auf alles außer `in_pruefung`/`abgelehnt` (Korrektur-Modus nach Annahme).
	 * `canEditPoint` deckt zugleich den Frozen-Lock ab (admin → false bei frozen),
	 * der explizite `isPointFrozen`-Check bleibt als schnelle Vorab-Sperre.
	 *
	 * UI-Stellen, die auf einen konkreten Punkt schreiben (Felder, Save-Button),
	 * sollten diese Methode nutzen.
	 */
	/**
	 * Mehrfachrollen-Kontext (Union, MR — PERMISSIONS.md §3 MR) für die
	 * Lib-Punkt-Funktionen. Ersetzt den `getWorkflowRole`-Kollaps (@deprecated,
	 * 2026-06-18): statt einer einzigen Hauptrolle (die den Bearbeiter-Anteil
	 * eines Doppelrollen-Users unterdrückte — Befunde Bug 1/3) werden ALLE
	 * Mitgliedschaften als Flags durchgereicht. `canIbe(...)` ist projekt-aware
	 * (Keycloak-Rolle UND Member-Eintrag, Override Z.1018).
	 */
	private workflowRoleFlags(): WorkflowRoleFlags {
		return {
			isAdmin: this.canIbe('entwickler'),
			isBearbeiter: this.canIbe('bearbeiter'),
			isKontrolleur: this.canIbe('kontrolleur')
		};
	}

	canIWritePoint(point: WorkflowDocument | null | undefined): boolean {
		if (!this.canI('write', 'points')) return false;
		// Harter Projekt-Lock (Lebenszyklus): explizit abgeschlossenes Projekt
		// friert ALLES ein — auch Owner/Entwickler.
		if (this.isCurrentProjectFinalized()) return false;
		if (this.isPointFrozen(point)) return false;
		if (!point) return false;
		// Cross-Projekt-Schutz auf Feld-Ebene (Bug A): Ein Punkt, dessen
		// workflow.projectId auf ein ANDERES als das aktive Projekt zeigt, darf
		// hier nicht editiert werden — auch nicht vom Owner/Entwickler. Symmetrisch
		// zum Status-Tor (WorkflowService.availableTransitions/transition). Der
		// isPointFrozen-Check oben greift nur bei `abgeschlossen`.
		if (belongsToOtherProject(point, untracked(() => this.project()))) return false;
		// Mehrfachrollen-Union (MR, 2026-06-18): kein getWorkflowRole-Kollaps mehr —
		// ein Doppelrollen-User (B+K) editiert eigene `bearbeitung`-Punkte
		// (Bearbeiter-Anteil) UND `in_pruefung`/`abgelehnt` (Kontrolleur-Anteil).
		const own = isOwnPoint(point, this._auth.currentUserId);
		return canEditPoint(point, 'none', own, this.workflowRoleFlags());
	}

	/**
	 * Darf der aktuelle User einen Punkt beim **Projekt-Restore** ueberschreiben?
	 *
	 * Wraps {@link canOverwriteOnRestore} (Lib) + die App-spezifischen Kontext-
	 * Gates (Projekt-Lock, Frozen-Punkt). Spec: ein Punkt darf ueberschrieben
	 * werden, wenn der Akteur ihn direkt editieren koennte ODER ihn per Status-
	 * Transition in einen editierbaren Status versetzen koennte (PERMISSIONS.md
	 * Backup/Restore-Tabelle, User-Wunsch 2026-06-06).
	 *
	 * - Bearbeiter: bearbeitung/kontrolle UND `isOwnPoint`
	 * - Kontrolleur: kontrolle/in_pruefung/abgeschlossen/abgelehnt (kein Owner-Check)
	 * - Entwickler (admin): jeder Status (Bestand inkl.)
	 *
	 * LOCK (Projekt finalisiert) und Frozen-Punkt: nie ueberschreibbar — auch
	 * nicht fuer Entwickler. Cross-Projekt-Schutz wird hier bewusst NICHT
	 * geprueft: ein Restore-Punkt traegt im Backup seine `workflow.projectId`,
	 * die nach dem Schreiben sowieso auf das wiederhergestellte Projekt zeigt
	 * (Backup-Identity).
	 */
	canIRestoreOverwritePoint(point: WorkflowDocument | null | undefined): boolean {
		if (!point) return false;
		// Auth-Permission (App-global, NICHT projekt-aware): beim Restore-Preview
		// ist typischerweise noch kein Projekt aktiv. Mit `this.canI(...)` (projekt-
		// aware) wuerde der Check auf die Datennutzer-Default-Rolle zurueckfallen
		// und alle Punkte blockieren — der Bearbeiter saehe in der Wizard-Preview
		// nichts als blocked. `auth.canI` antwortet rolle-basiert ohne Projekt-
		// Kontext, was die korrekte Frage fuer den Restore-Pfad ist.
		if (!this._auth.canI('write', 'points')) return false;
		// LOCK des AKTIVEN Projekts (falls eines geladen ist) bleibt absolut —
		// kein Restore in ein finalisiertes Projekt.
		if (this.isCurrentProjectFinalized()) return false;
		// Punkt-Frozen-Check (`isPointFrozen` = abgeschlossen+fremdes Projekt)
		// ist im Restore-Kontext NICHT anzuwenden: das aktive Projekt waehrend
		// des Wizards ist typischerweise keins (Modal frisch geoeffnet) oder
		// ein anderes als das Backup-Ziel — `workflow.projectId !== this.project()`
		// wuerde dann jeden abgeschlossen-Punkt als frozen markieren und der
		// Kontrolleur saehe seine erlaubten Reopen-Pfade als blocked. Statt
		// dessen entscheidet `canOverwriteOnRestore` rein status-basiert pro
		// Rolle (s. PERMISSIONS.md §7 Backup/Restore-Tabelle).
		// Mehrfachrollen-Union (MR, 2026-06-18): kein getWorkflowRole-Kollaps mehr.
		const own = isOwnPoint(point, this._auth.currentUserId);
		return canOverwriteOnRestore(point, 'none', own, this.workflowRoleFlags());
	}

	/**
	 * Prüft eine Rollen-Permission direkt gegen die RoleDefinition.
	 */
	private checkRolePermission(role: string, access: string, resource: string): boolean {
		const matrix = RoleDefinition.get(role);
		if (!matrix) return false;
		const res = matrix.get(resource);
		return res?.has(access) ?? false;
	}

	/**
	 * Projekt-aware Berechtigungsprüfung.
	 */
	canI(access: string, resource: string): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());

		if (!projectDoc) {
			if (resource === 'points') {
				return this.checkRolePermission('datennutzer', access, resource);
			}
			return this._auth.canI(access, resource);
		}

		// Entwickler-Bypass: volle Capabilities unabhaengig von Projekt-Mitgliedschaft.
		// Ohne diesen Bypass wuerde ein Entwickler, der nicht als Bearbeiter im Projekt
		// eingetragen ist, beim Klick auf Highlight-Areas leer ausgehen, weil keine der
		// folgenden Branches greifen (kein Eintrag in projectDoc.bearbeiter).
		if (this._auth.canIbe('entwickler')) {
			return this._auth.canI(access, resource);
		}

		// Owner-Punkt-Rechte-Bypass entfernt (Konzept-Aenderung 2026-05-28,
		// PERMISSIONS.md §1.2 + §5.2a): Owner = Manager am eigenen Projekt;
		// fuer Punkt-Aktionen braucht es einen Member-Eintrag (Bearbeiter/
		// Kontrolleur). Der Owner-Sicht-Bypass laeuft separat ueber
		// workflow.service `canSeePoint(..., isOwnerOfActiveProject=true)`.
		if (this._auth.canIbe('bearbeiter') && this.isBearbeiter()) {
			return this.checkRolePermission('bearbeiter', access, resource);
		} else if (this._auth.canIbe('kontrolleur') && this.isKontrolleur()) {
			return this.checkRolePermission('kontrolleur', access, resource);
		}

		// Manager (Keycloak): Projekt-Management-Hierarchie über Owner (G2). Auf Punkte
		// nur lesend (M2/M3) — die manager-RoleDefinition hat points: read+checkout.
		if (this._auth.canIbe('manager')) {
			return this.checkRolePermission('manager', access, resource);
		}

		if (this._auth.isDatennutzer()) {
			return this.checkRolePermission('datennutzer', access, resource);
		}

		return false;
	}

	canIadd(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (isProjectFinalized(projectDoc)) return false; // harter Projekt-Lock (Lebenszyklus)

		if (this._auth.canIbe('entwickler')) {
			return this._auth.canI('add', 'points');
		}

		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a): Owner ohne Bearbeiter-
		// Eintrag darf keine Punkte anlegen. Member-Pflicht wird beim Projekt-
		// Anlegen via §4a-Modal erzwungen.
		if (!this.isBearbeiter()) return false;
		if (!this._auth.canIbe('bearbeiter')) return false;
		return this.checkRolePermission('bearbeiter', 'add', 'points');
	}

	canIremove(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;

		if (this._auth.canIbe('entwickler')) {
			return true;
		}

		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a).
		if (!this.isBearbeiter()) return false;
		if (!this._auth.canIbe('bearbeiter')) return false;
		return this.checkRolePermission('bearbeiter', 'write', 'points');
	}

	/**
	 * Per-Punkt-Lösch-Gate (Matrix §4.2 B8). Anders als `canIremove()` (reiner
	 * Rollen-Check für die Button-Sichtbarkeit) prüft dies den KONKRETEN Punkt:
	 *  - Entwickler: Bypass (aber Cross-Projekt-Schutz bleibt — Bug A symmetrisch).
	 *  - Owner: volle Punktrechte im EIGENEN Projekt (Entscheidung #3), aber
	 *    NICHT für Punkte, die zum Workflow eines anderen Projekts gehören.
	 *  - Bearbeiter-Member: NUR als AKTUELLER Inhaber und in editierbarem Status
	 *    (`canEditPoint` = eigen + `bearbeitung`) und nicht eingefroren. Fremde,
	 *    `kontrolle`/`in_pruefung`/`abgelehnt` und (fremd-)abgeschlossene → ✗.
	 *    Inhaber = `workflow.editors[last]` (Greifer via „In Bearbeitung nehmen"),
	 *    sonst `workflow.createdBy.id` (Weg B, 2026-06-17). KEIN permissive Null-
	 *    Fallback: ein Punkt ohne dokumentierten Inhaber ist NICHT löschbar
	 *    (Loeschen ist destruktiv; der permissive `isOwnPoint`-Null-Fallback aus
	 *    `canEditPoint` darf hier nicht greifen).
	 *  - Kontrolleur/Manager/Datennutzer: nie (Löschen ist kein Teil ihrer Matrix).
	 *
	 * Symmetrisch zu `canIWritePoint`: wer den Punkt nicht editieren darf, darf
	 * ihn erst recht nicht löschen. Cross-Projekt-Block (Bug A) wirkt für ALLE
	 * Rollen — das Löschen aus dem aktiven Projekt heraus darf den Workflow
	 * eines anderen Projekts nicht beruehren.
	 */
	canIRemovePoint(point: WorkflowDocument | null | undefined): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc || !point) return false;
		if (isProjectFinalized(projectDoc)) return false; // harter Projekt-Lock (Lebenszyklus)

		// Frozen + Cross-Projekt vor allen Rollen-Bypass-Pruefungen (Bug A
		// symmetrisch fuer das Loesch-Gate): gilt auch fuer Owner/Entwickler.
		if (this.isPointFrozen(point)) return false;
		if (belongsToOtherProject(point, untracked(() => this.project()))) return false;

		if (this._auth.canIbe('entwickler')) return true;
		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a): Loeschen ist Bearbeiter-
		// Sache (eigene Punkte); Owner ohne Bearbeiter-Member darf nicht loeschen.

		if (!this.isBearbeiter() || !this._auth.canIbe('bearbeiter')) return false;

		// Inhaber-Modell beim Loeschen (Weg B, 2026-06-17, User-Entscheidung):
		// Massgeblich ist der AKTUELLE Inhaber — wer den Punkt durch „In Bearbeitung
		// nehmen" gegriffen hat (`workflow.editors[last]`), darf ihn loeschen, auch
		// wenn der Ersteller-Stempel (`createdBy`) ein anderer ist (z. B. der
		// Migrations-Platzhalter 'migration' aus v16→v17). Konsistent mit
		// `isOwnPoint`/`canEditPoint` (editors[last] ?? createdBy), die das Editier-
		// Eigentum schon so bestimmen. Fehlt der Inhaber-Verlauf, zaehlt wie bisher
		// `createdBy`. WICHTIG: kein permissive Null-Fallback — ein Punkt OHNE
		// dokumentierten Inhaber (kein editors, kein/leerer createdBy) ist NICHT
		// loeschbar (sonst koennte ein Bearbeiter herrenlose Punkte loeschen).
		const editors = point.workflow?.editors;
		const inhaber = (editors && editors.length > 0)
			? editors[editors.length - 1]?.id
			: point.workflow?.createdBy?.id;
		if (!inhaber || inhaber !== this._auth.currentUserId) return false;

		// Löschen ist ein BEARBEITER-Recht (§5.1, nur eigener `bearbeitung`-Punkt) —
		// die Bearbeiter-Mitgliedschaft ist oben (GATE 1) bereits sichergestellt.
		// Daher bewusst der reine Bearbeiter-Anteil (kein Kontrolleur-Lösch-Recht):
		// ein Doppelrollen-User (B+K) darf seinen eigenen `bearbeitung`-Punkt löschen
		// (Bug 3 behoben), aber NICHT einen `in_pruefung`/`abgelehnt`-Punkt über den
		// Kontrolleur-Anteil. Kein getWorkflowRole-Kollaps mehr (@deprecated).
		return canEditPoint(point, 'bearbeiter', /*isOwnPoint=*/ true);
	}

	/**
	 * Kontrolleur-Reopen (Lebenszyklus 2026-05-27 / 2026-06-01): Darf der
	 * aktuelle User einen abgeschlossenen ODER abgelehnten Punkt zurück „in die
	 * Kontrolle" (→ `in_pruefung`) holen?
	 *
	 * Erlaubt für JEDEN eingetragenen Projekt-Kontrolleur (Urlaubsvertretung —
	 * bewusst NICHT an den persönlichen Abschluss gebunden) sowie Entwickler.
	 * Owner ohne Kontrolleur-Eintrag NICHT (konsistent mit canIfreigeben/D1).
	 * Bedingungen: Punkt-Status `abgeschlossen` oder `abgelehnt`, Projekt NICHT
	 * explizit abgeschlossen (harter Lock sticht auch den Entwickler), kein
	 * Fremdprojekt. Die Mutation selbst macht `reopenToReview` (luzula-lib).
	 */
	canIReopenPoint(point: WorkflowDocument | null | undefined): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc || !point) return false;
		if (isProjectFinalized(projectDoc)) return false;
		const status = effectiveStatus(point);
		if (status !== 'abgeschlossen' && status !== 'abgelehnt') return false;
		if (belongsToOtherProject(point, untracked(() => this.project()))) return false;
		return this.canIbe('kontrolleur') || this._auth.canIbe('entwickler');
	}

	canIimport(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		// Harter Projekt-Lock (Lebenszyklus, 2026-07-11): Ein endgueltig
		// abgeschlossenes Projekt friert ALLES ein — Import legt Punktdaten an und
		// steht damit auf einer Stufe mit `canIadd` (§5.1) und dem Restore-LOCK.
		// Vor dem Entwickler-Bypass, symmetrisch zu canIadd: der Lock sticht jede Rolle.
		if (isProjectFinalized(projectDoc)) return false;

		// Entwickler = Bypass. Manager NICHT: Import/Reimport legt bzw. schreibt
		// Punktdaten an, und Manager = "Projekt-Management ohne fachliche
		// Punktbearbeitung" (Plan §1.1; Punkt-Matrix M2/M3 = ✗; role-model
		// manager = points {read, checkout}, kein add/write). Die Workflow-Status-
		// Klemme beim Reimport (canIfreigeben) ist nur eine Sicherung gegen Status-
		// Fälschung, kein Import-Grant für Manager.
		if (this._auth.canIbe('entwickler')) {
			return true;
		}

		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a): Import legt Punktdaten
		// an und ist damit Bearbeiter-Sache; Owner ohne Bearbeiter-Member darf
		// nicht importieren.
		if (!this.isBearbeiter()) return false;
		if (!this._auth.canIbe('bearbeiter')) return false;
		return this.checkRolePermission('bearbeiter', 'add', 'points');
	}

	/**
	 * Darf der aktuelle User Projekt-Mitglieder (Bearbeiter/Kontrolleure) zuordnen?
	 * G3 / P7: Owner (inkl. Bearbeiter-Owner), Keycloak-Manager oder Entwickler.
	 */
	canIinvite(): boolean {
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	/**
	 * Darf der aktuelle User Altpunkte/abgeschlossene Punkte zur Bearbeitung
	 * freigeben (Workflow-Reset → `bearbeitung`)? G6 / P8/P9/P11: Keycloak-
	 * Manager + Entwickler-Bypass. Bewusst NICHT Owner: Ein Bearbeiter koennte
	 * sich sonst durch Anlegen eines eigenen Projekts den Owner-Status
	 * erschleichen und damit fremde abgeschlossene Punkte aufmachen (User-
	 * Entscheidung 2026-05-26). Reine Rollen-Capability — die Punkt-Status-
	 * Pruefung (nur Altpunkt: status null/abgeschlossen) erfolgt am Aufrufort.
	 */
	canIfreigeben(): boolean {
		return this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	/**
	 * Komposition: Soll der „Zur Bearbeitung freigeben / In dieses Projekt
	 * übernehmen"-Button für einen konkreten Punkt sichtbar sein?
	 *
	 *  - Rolle erlaubt Freigabe (`canIfreigeben`, Manager/Entwickler);
	 *  - aktives Projekt vorhanden (sonst gibt es kein Ziel);
	 *  - Punkt ist Altpunkt (`status` null/abgeschlossen) — sonst gibt es nichts
	 *    freizugeben.
	 *
	 * **Reaktivierung im eigenen Projekt (2026-06-17, User-Entscheidung):** Der
	 * frühere Block „nur wenn der Punkt NICHT bereits dem aktiven Projekt
	 * zugeordnet ist" (No-Op-Vermeidung M-P9, 2026-06-02) ist entfernt. Seit die
	 * Freigabe den Workflow auf `anstehend` (Pool) zurücksetzt, ist sie auch für
	 * einen abgeschlossenen Punkt IM aktiven Projekt eine sinnvolle Aktion
	 * (Reaktivierung) — und nicht mehr nur ein „Hereinholen" aus einem fremden
	 * Projekt. Hintergrund: Die v16→v17-Migration hat Bestand-Punkte mit
	 * Projekt-Zuordnung zu „abgeschlossen" gemacht; ihr Ersteller (Bearbeiter)
	 * kam sonst an die eigenen Punkte nicht mehr heran (PERMISSIONS.md §4 P9/P11,
	 * D1). Status bleibt das Tor: laufende Stati (anstehend/bearbeitung/kontrolle/
	 * in_pruefung/abgelehnt) gibt es nicht „freizugeben".
	 */
	canIfreigebenPoint(point: { header?: { projects?: { id: string }[] }; workflow?: { status?: any; projectId?: string } } | null | undefined): boolean {
		if (!point) return false;
		if (!this.canIfreigeben()) return false;
		const active = this.project();
		if (!active) return false;
		const status = point.workflow?.status ?? null;
		// Laufende Stati (anstehend/bearbeitung/kontrolle/in_pruefung/abgelehnt)
		// gibt es nicht „freizugeben" — nur Altpunkte (Bestand/abgeschlossen).
		if (status !== null && status !== 'abgeschlossen') return false;
		// Problem B (User-Entscheidung 2026-06-18, revidiert D1 von 2026-06-17):
		// Ein abgeschlossener Punkt, dessen WORKFLOW zum AKTUELLEN Projekt gehört
		// (`workflow.projectId === aktiv`), wird NICHT mehr zur Bearbeitung
		// freigegeben (→anstehend) — er gehört hierher und wird nur „zur Prüfung
		// zurückgeholt" (Reopen, §5.2). Die →anstehend-Freigabe ist die ÜBERNAHME
		// fremder bzw. projektloser Altpunkte (anderes Projekt oder kein
		// workflow.projectId = Bestand / per v16→v17 migriert).
		if (status === 'abgeschlossen' && point.workflow?.projectId === active) return false;
		return true;
	}

	/**
	 * Komposition: Soll der „Übernahme zurücknehmen"-Button für einen konkreten
	 * Punkt sichtbar sein? Umkehrung von `canIfreigebenPoint` (User-Wunsch
	 * 2026-06-19): einen per Übernahme (abgeschlossen → anstehend) ins aktive
	 * Projekt geholten Punkt wieder auf `abgeschlossen` setzen und dem vorigen
	 * Projekt zurückgeben (oder, falls es das Projekt nicht mehr gibt, zum
	 * Bestand machen).
	 *
	 *  - Rolle IDENTISCH zur Übernahme (`canIfreigeben`, Manager/Entwickler —
	 *    Owner nur mit Manager-Rolle; bewusst KEIN Owner-Bypass, gleiche
	 *    Sicherheitsregel wie die Übernahme, User-Entscheidung 2026-06-19);
	 *  - aktives Projekt vorhanden;
	 *  - Punkt-Status `anstehend` UND dem aktiven Projekt zugeordnet (kein
	 *    Fremd-Projekt — Cross-Projekt-Schutz);
	 *  - es ist eine ECHTE Übernahme: `takeoverPreviousProject` liefert ein
	 *    Ergebnis (frischer Reset-Marker). Eine bloße Pool-Rückgabe
	 *    (bearbeitung → anstehend) ist KEINE rücknehmbare Übernahme.
	 */
	canIzuruecknehmenPoint(point: WorkflowDocument | null | undefined): boolean {
		if (!point) return false;
		if (!this.canIfreigeben()) return false;
		const active = this.project();
		if (!active) return false;
		if (effectiveStatus(point) !== 'anstehend') return false;
		if (belongsToOtherProject(point, active)) return false;
		return takeoverPreviousProject(point) !== null;
	}

	/**
	 * Darf der aktuelle User das aktive Projekt EXPLIZIT abschließen (Lebenszyklus
	 * 2026-05-27 / PERMISSIONS.md §6.2)? Manager/Entwickler + Kontrolleur-Member
	 * (User-Entscheidung 2026-05-28: ein Owner allein ohne Manager/Kontrolleur-
	 * Rolle darf das Projekt NICHT final abschließen). Nur erlaubt, wenn das
	 * Projekt „Bereit zum Abschluss" ist (abgeleiteter Status `abgeschlossen` =
	 * alle Punkte fertig) und noch nicht finalisiert. Setzt danach den harten
	 * Lock (`finalizeProject`).
	 */
	canIfinalizeProject(): boolean {
		const auth = this._auth;
		const isKontrolleurMember = auth.canIbe('kontrolleur') && this.isKontrolleur();
		if (!(auth.canIbe('manager') || auth.canIbe('entwickler') || isKontrolleurMember)) return false;
		const doc = untracked(() => this.currentProjectDoc());
		if (!doc || isProjectFinalized(doc)) return false;
		return (doc.workflow?.status ?? null) === 'abgeschlossen';
	}

	/**
	 * Darf der aktuelle User ein explizit abgeschlossenes Projekt wieder öffnen
	 * (Lebenszyklus 2026-05-27 / PERMISSIONS.md §6.2, symmetrisch zu Finalize)?
	 * Manager/Entwickler + Kontrolleur-Member; nur wenn finalisiert. Hebt den
	 * harten Lock auf (`reopenProject`).
	 */
	canIreopenProject(): boolean {
		const auth = this._auth;
		const isKontrolleurMember = auth.canIbe('kontrolleur') && this.isKontrolleur();
		if (!(auth.canIbe('manager') || auth.canIbe('entwickler') || isKontrolleurMember)) return false;
		return isProjectFinalized(untracked(() => this.currentProjectDoc()));
	}

	/**
	 * Archiviert ein Projekt bzw. nimmt die Archivierung zurück. Rein
	 * organisatorisch (Projektauswahl blendet Archiviertes aus) — KEINE
	 * Rechteänderung, nichts wird eingefroren. Erlaubt analog „Projekt löschen"
	 * (P3): Owner des Projekts, Keycloak-Manager oder Entwickler. Defense-in-depth:
	 * geprüft wird gegen den geladenen Doc-Owner, nicht nur das aktive Projekt.
	 * Liefert false bei fehlender Berechtigung oder unbekanntem Projekt.
	 */
	async setProjectArchived(projectId: string, archived: boolean): Promise<boolean> {
		if (!projectId) return false;
		const doc = await this.getOne(projectId) as ProjectDocument | undefined;
		if (!doc) return false;
		const mayManage = this._auth.canIbe('manager') || this._auth.canIbe('entwickler')
			|| doc.owner?.id === this._auth.currentUserId;
		if (!mayManage) return false;
		if (!!doc.archived !== archived) {
			doc.archived = archived;
			await this.save(doc);
		}
		await this.refreshProjectList();
		return true;
	}

	/**
	 * Schließt ein Projekt EXPLIZIT ab (Lebenszyklus 2026-05-27): setzt
	 * `workflow.finalizedAt` → harter Lock (alles eingefroren, auch Entwickler).
	 * Nur Manager/Entwickler und nur aus „Bereit zum Abschluss" (abgeleiteter
	 * Status `abgeschlossen`). Idempotent. Liefert false bei fehlender Berechtigung,
	 * unbekanntem Projekt oder wenn das Projekt nicht abschlussbereit ist.
	 */
	async finalizeProject(projectId?: string): Promise<boolean> {
		const id = projectId ?? untracked(() => this.project());
		if (!id) return false;
		const doc = await this.getOne(id) as ProjectDocument | undefined;
		if (!doc || !doc.workflow) return false;
		// PERMISSIONS.md §6.2: Manager/Entwickler ODER Kontrolleur-Member.
		// Kontrolleur-Status hängt vom konkreten Projekt-Doc ab (`kontrolleure[]`)
		// — daher hier per Doc-Lookup statt isKontrolleur() (das auf das AKTIVE
		// Projekt zeigt; finalizeProject kann auch ohne aktives Projekt aufgerufen
		// werden, wenn `projectId` explizit übergeben wird).
		const isKontrolleurMember = this._auth.canIbe('kontrolleur')
			&& Array.isArray(doc.kontrolleure)
			&& doc.kontrolleure.some(k => k?.id === this._auth.currentUserId);
		if (!(this._auth.canIbe('manager') || this._auth.canIbe('entwickler') || isKontrolleurMember)) return false;
		if (doc.workflow.status !== 'abgeschlossen') return false; // nur „Bereit zum Abschluss"
		if (!doc.workflow.finalizedAt) {
			doc.workflow = {
				...doc.workflow,
				finalizedAt: { by: this._auth.currentUserId, byName: this._auth.currentUser, at: Date.now() }
			};
			await this.save(doc);
			await this.refreshProjectList();
		}
		return true;
	}

	/**
	 * Öffnet ein explizit abgeschlossenes Projekt wieder (Lebenszyklus 2026-05-27):
	 * löscht `workflow.finalizedAt` → harter Lock aufgehoben. Der abgeleitete
	 * Status bleibt (i.d.R. `abgeschlossen` → wieder „Bereit zum Abschluss"). Nur
	 * Manager/Entwickler. Idempotent. Liefert false bei fehlender Berechtigung
	 * oder unbekanntem Projekt.
	 */
	async reopenProject(projectId?: string): Promise<boolean> {
		const id = projectId ?? untracked(() => this.project());
		if (!id) return false;
		const doc = await this.getOne(id) as ProjectDocument | undefined;
		if (!doc) return false;
		// PERMISSIONS.md §6.2: symmetrisch zu finalizeProject — Manager/Entwickler
		// ODER Kontrolleur-Member. Member-Check am Doc, siehe Begründung in
		// finalizeProject.
		const isKontrolleurMember = this._auth.canIbe('kontrolleur')
			&& Array.isArray(doc.kontrolleure)
			&& doc.kontrolleure.some(k => k?.id === this._auth.currentUserId);
		if (!(this._auth.canIbe('manager') || this._auth.canIbe('entwickler') || isKontrolleurMember)) return false;
		if (doc.workflow?.finalizedAt) {
			const { finalizedAt: _drop, ...rest } = doc.workflow;
			doc.workflow = rest as ProjectWorkflowState;
			await this.save(doc);
			await this.refreshProjectList();
		}
		return true;
	}

	/**
	 * Darf der aktuelle User den Kartierer eines Punkts umhängen (Datenfeld
	 * `header.aufnahme.kartierer` + gekoppeltes Eigentum `workflow.createdBy.id`)?
	 *
	 * Bewusst NICHT `canIfreigeben()`: Manager dürfen Punkt*daten* nicht ändern
	 * (Matrix M2 „Punkte editieren = ✗"; role-model Manager = points:read,checkout).
	 * Die Altpunkt-Freigabe ist erlaubt, weil sie nur den Workflow-*Status* setzt,
	 * nicht Datenfelder. Kartierer umhängen berührt ein Datenfeld → nur Owner
	 * (volle Punktrechte, Phase C) und Entwickler (Bypass). Ein normaler
	 * Bearbeiter darf das Eigentum ebenfalls nicht verschieben.
	 */
	canIReassignKartierer(): boolean {
		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a): Kartierer-Umhaengen
		// aendert ein Datenfeld; ist damit Bearbeiter-Sache. Owner ohne
		// Bearbeiter-Member darf das nicht.
		if (this._auth.canIbe('entwickler')) return true;
		return this._auth.canIbe('bearbeiter') && this.isBearbeiter();
	}

	/**
	 * Darf der aktuelle User den Batch-Edit-Assistenten nutzen (Phase I, BE1)?
	 * = Punkt-Schreibrecht im aktuellen Projekt (Bearbeiter/Kontrolleur-Member,
	 * Owner, Entwickler). Manager (kein Punkt-Write) und Datennutzer → nein.
	 * Das Per-Punkt-Gate beim Execute liefert zusätzlich `canIWritePoint` (G8).
	 */
	canIbatchEdit(): boolean {
		return this.canI('write', 'points');
	}

	/**
	 * Darf der aktuelle User einen gespeicherten Batch zurückrollen (Phase I,
	 * G9/BE4/BE5)?
	 *  - Manager/Entwickler dürfen JEDEN Batch zurückrollen (canIfreigeben-Pfad).
	 *  - Sonst nur der **Initiator** des eigenen Batches (Vergleich über den
	 *    Username, da die Batch-Historie den Username speichert).
	 *  - Legacy-Einträge ohne Initiator (G11) → nur die Privilegierten oben.
	 *
	 * Das Überspringen inzwischen eingefrorener Punkte (G10) ist unabhängig davon
	 * und wird beim Undo per `canWritePoint`-Predicate erzwungen. Der **Owner**
	 * ist hier NICHT privilegiert (Owner-Bypass entfernt, Konzept 2026-05-28 /
	 * PERMISSIONS.md §5.2a): Ein Owner ohne Manager-/Entwickler-Rolle darf nur
	 * seinen EIGENEN Batch (als Initiator) zurückrollen, keine fremden.
	 */
	canIundoBatch(summary: { user?: string } | null | undefined): boolean {
		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a). Manager/Entwickler
		// duerfen weiterhin jeden Batch zurueckrollen (canIfreigeben-Pfad).
		if (this.canIfreigeben()) return true;
		// Initiator-Pfad nur für aktuell schreibberechtigte Mitglieder — ein reiner
		// Datennutzer (kein Punkt-Write) darf nie zurückrollen, auch wenn der
		// Username zufällig zum Batch-Initiator passt.
		if (!this.canIbatchEdit()) return false;
		const initiator = summary?.user;
		if (!initiator) return false;
		return initiator === this._auth.currentUser;
	}

	// canIbe / isOwner / amIMember sind seit Route B member-aware Defaults in
	// BaseProjectService — konfiguriert über PROJECT_ROLES_CONFIG.memberRoles
	// (appRole bearbeiter→Feld `bearbeiter`, kontrolleur→`kontrolleure`). bpweb
	// erbt sie unverändert; hier bleiben nur `amI` (Delegation) + die benannten
	// Member-Wrapper, die der restliche bpweb-Code (canEditDrawings etc.) nutzt.

	/** Member-aware Rollenprüfung — delegiert an den geerbten {@link canIbe}. */
	amI(role: string): boolean { return this.canIbe(role); }

	/** Ist der aktuelle User Bearbeiter-Member des aktiven Projekts? */
	isBearbeiter(): boolean { return this.isProjectMember('bearbeiter'); }

	/** Ist der aktuelle User Kontrolleur-Member des aktiven Projekts? */
	isKontrolleur(): boolean { return this.isProjectMember('kontrolleure'); }

	/**
	 * Ist der Punkt dem AKTIVEN Projekt zugeordnet? Single-Source der zuvor 3×
	 * inline reimplementierten `isAssignedToActiveProject()` (hl-area.directive,
	 * bohrpunkt-tabs, bohrpunkt-plausi — PERMISSIONS.md §9 „Vereinheitlichung
	 * divergenter Call-Sites"). Liest `header.projects` (das UI-Editier-/
	 * Transitions-Gate), NICHT `workflow.projectId`. Ohne aktives Projekt oder
	 * ohne `projects`-Array: false. Komponiert mit `canIWritePoint` das Feld-Edit-
	 * Gate (§5.1): `canIWritePoint(bp) && isPointAssignedToActiveProject(bp)`.
	 */
	isPointAssignedToActiveProject(point: { header?: { projects?: { id: string }[] } } | null | undefined): boolean {
		const projectId = this.project();
		if (!projectId) return false; // Kein Projekt aktiv → nicht bearbeitbar
		const projects = point?.header?.projects;
		if (!projects) return false; // Kein projects-Array → nicht zugewiesen
		return projects.some(p => p.id === projectId);
	}

	/**
	 * Darf der User für Punkte die **Bearbeiter-/Edit-Sicht** einnehmen?
	 * = eingetragener Projekt-Bearbeiter ODER Entwickler (Bypass).
	 *
	 * Owner-Bypass entfernt (Konzept-Aenderung 2026-05-28, PERMISSIONS.md
	 * §5.2a): Owner ohne Bearbeiter-Member-Eintrag bekommt keinen
	 * Bearbeiter-Modus mehr — er ist read-only-Betrachter (admin-Sicht-
	 * Bypass laeuft separat in workflow.service.canSeePoint). Manager NICHT
	 * (kein Punktdaten-Edit, M2).
	 */
	canActAsBearbeiter(): boolean {
		return this.canIbe('bearbeiter') || this._auth.canIbe('entwickler');
	}

	/**
	 * Darf der User für Punkte die **Kontrolleur-/Review-Sicht** einnehmen?
	 * = eingetragener Projekt-Kontrolleur ODER Entwickler (Bypass). Owner-
	 * Bypass entfernt (siehe canActAsBearbeiter).
	 */
	canActAsKontrolleur(): boolean {
		return this.canIbe('kontrolleur') || this._auth.canIbe('entwickler');
	}

	canModifyProject(): boolean {
		// Wenn der User eine echte Rolle hat (roles-Set gefüllt), ist er authentifiziert —
		// unabhängig vom BehaviorSubject-State, der nach Token-Refresh-Problemen
		// oder Auto-Logout inkonsistent sein kann
		if (this._auth.canIbe('manager') || this._auth.canIbe('bearbeiter') ||
			this._auth.canIbe('kontrolleur') || this._auth.canIbe('entwickler')) return true;
		if (!this._auth.isAuthenticated.getValue()) return false;
		if (this._auth.isDatennutzer()) return false;
		return true;
	}

	override canEditRegions(): boolean {
		if (!this.canModifyProject()) return false;
		if (this.isCurrentProjectFinalized()) return false; // harter Projekt-Lock (Lebenszyklus)
		// §4.1 P6: Owner/Manager — plus Entwickler (Bypass „alles, alles, immer").
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	canDeleteProject(): boolean {
		if (!this.canModifyProject()) return false;
		// §4.1 P3: Owner/Manager — plus Entwickler (Bypass).
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	/**
	 * Rechtematrix §4.1 P1: Wer darf ein NEUES Projekt anlegen (→ wird dessen
	 * Owner)? bearbeiter / manager / entwickler. NICHT: reiner Kontrolleur,
	 * Datennutzer. Prüft die Keycloak-Rolle direkt (bei der Anlage ist i.d.R. kein
	 * Projekt aktiv). Wird vom Lib-`project-manager` über `canICreateProject`
	 * konsumiert (gatet die „Neu"-Aktion).
	 */
	canICreateProject(): boolean {
		if (this._auth.canIbe('entwickler')) return true;
		if (this._auth.canIbe('manager')) return true;
		return this._auth.canIbe('bearbeiter');
	}

	/**
	 * Zeichnungen (Zeichnungslayer am Projekt) anlegen/bearbeiten/löschen.
	 * Analog zu Punkten, aber am PROJEKT-Status: nur **Bearbeiter-Member / Owner /
	 * Entwickler**, und nur solange das Projekt **nicht abgeschlossen** ist (dann
	 * eingefroren). Kontrolleur/Manager/Datennutzer/Nicht-Member: nein.
	 * (Kontrolleur hat zwar `points:write` für die Korrektur, soll aber nicht
	 * zeichnen — daher bewusst NICHT über `canI('write','points')`.)
	 */
	/**
	 * Flächen des Karten-Editors (Standort/Humusform) ändern.
	 *
	 * Heute **nur Entwickler** — so ist der Editor gebaut (Menüeintrag „Flächen
	 * bearbeiten" ist für alle anderen ausgegraut).
	 *
	 * **Diese Methode ist die EINZIGE Stelle, an der das steht** (Leiste und
	 * Konfliktklärung fragen beide hier). Wird der Editor eines Tages für
	 * weitere Rollen geöffnet, zieht das Klären zweier Fassungen von selbst mit
	 * — genau das war die Auflage des Nutzers am 11.09.2026 („wenn das mal für
	 * andere Rollen freigegeben wird, muss das natürlich nachgezogen werden").
	 * Die Regel abzuschreiben wäre eine zweite Wahrheit, die irgendwann
	 * auseinanderläuft.
	 */
	canEditGeomFeatures(): boolean {
		return this._auth.canIbe('entwickler');
	}

	canEditDrawings(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		// Harter Lock erst beim EXPLIZITEN Abschluss (Lebenszyklus): „Bereit zum
		// Abschluss" (abgeleitet `abgeschlossen`, nicht finalisiert) friert NICHT ein.
		if (isProjectFinalized(projectDoc)) return false;
		if (this._auth.canIbe('entwickler')) return true;
		// Owner-Bypass entfernt (PERMISSIONS.md §5.2a): Zeichnen ist Bearbeiter-
		// Sache (Datenanlage); Owner ohne Bearbeiter-Member darf nicht zeichnen.
		return this.isBearbeiter() && this._auth.canIbe('bearbeiter');
	}

	/**
	 * Layer-KONFIGURATION ändern: Stil/Farbe, Anlegen/Löschen/Umbenennen von
	 * Zeichnungslayern, Stil-Overrides für GPKG-/GeoJSON-Layer. Enger als
	 * `canEditDrawings`: das eigentliche *Zeichnen* (Geometrie in einen Layer
	 * eintragen) bleibt für Bearbeiter-Member offen; die Layer-Landschaft
	 * kuratiert der Owner (Owner/Manager/Entwickler), nicht abgeschlossen-
	 * eingefroren.
	 * (PLAN-permissions Nachtrag 2026-05-26.)
	 */
	canEditLayerStyles(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		// Konsistent mit canEditDrawings: erst der EXPLIZITE Abschluss (Lebenszyklus)
		// friert ein — auch der Entwickler-Bypass greift dann nicht (Daten-Integrität).
		if (isProjectFinalized(projectDoc)) return false;
		if (this._auth.canIbe('entwickler')) return true;
		return this.isOwner() || this._auth.canIbe('manager');
	}

	/**
	 * Geodaten-Dateien verwalten: laden, neue Datenebene anlegen, umbenennen,
	 * Farbe, löschen (Ebenen-Fenster und Projektmenü).
	 *
	 * Grundsätzlich wie `canEditLayerStyles` (Owner/Manager/Entwickler). Setzt der
	 * Eigentümer im Projekt den Haken `workflowSettings.geodatenFuerBearbeiter`,
	 * dürfen es auch die eingetragenen Bearbeiter (Keycloak `role:kartierer`) —
	 * gebaut wie der Haken für die Sichtbarkeit der Punkte (Nutzer-Entscheidung
	 * 28.09.2026). Kontrolleure nicht. Ein explizit abgeschlossenes Projekt ist
	 * auch hier eingefroren.
	 * Zeichnungs-Ebenen und WMS bleiben bei `canEditLayerStyles`.
	 */
	darfGeodatenVerwalten(): boolean {
		if (this.canEditLayerStyles()) return true;
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (isProjectFinalized(projectDoc)) return false;
		if ((projectDoc as any).workflowSettings?.geodatenFuerBearbeiter !== true) return false;
		// Wie beim Zeichnen: Eintrag in bearbeiter[] UND die Rolle.
		return this.isBearbeiter() && this._auth.canIbe('bearbeiter');
	}

	/**
	 * Zeichnungen sehen: abgeschlossene Projekte sind öffentlich (read-only für
	 * alle); laufende nur für Mitglieder + Manager/Entwickler.
	 */
	canSeeDrawings(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (((projectDoc as any).workflow?.status ?? null) === 'abgeschlossen') return true;
		return this.amIMember() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	override canCreateRegions(): boolean {
		if (!this.canModifyProject()) return false;
		if (this.isCurrentProjectFinalized()) return false; // harter Projekt-Lock (Lebenszyklus)
		// Matrix §4.1 P6: Owner/Manager (Bearbeiter-Member ✗) — plus Entwickler (Bypass).
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	override canDeleteRegions(): boolean {
		if (!this.canModifyProject()) return false;
		if (this.isCurrentProjectFinalized()) return false; // harter Projekt-Lock (Lebenszyklus)
		// Matrix §4.1 P6: Owner/Manager (Bearbeiter-Member ✗) — plus Entwickler (Bypass).
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	/**
	 * Rollen-/Permission-Gate für Projekt-Management-Aktionen (Owner/Manager/
	 * Entwickler). Override der Basis-Methode.
	 *
	 * **Einfachmodell (PLAN-single-membership):** Die generische Mehrfach-Zuordnung
	 * von Punkten ist in bpweb abgeschafft (eine Bohrung gehört zu genau einem
	 * Projekt). Das App-Karten-Handler-Flag (`canAssign`/`canRemove`) gibt direkt
	 * `false` zurück; diese Methode wird daher nicht mehr als Zuordnungs-Gate
	 * genutzt. Sie bleibt als Rollen-Capability (Basis-Override) erhalten.
	 */
	override canAssignToProject(): boolean {
		if (!this.canModifyProject()) return false;
		return this.isOwner() || this._auth.canIbe('manager') || this._auth.canIbe('entwickler');
	}

	/**
	 * §4a.5 Migration beim Öffnen alter Projekte ohne Bearbeiter
	 * (PERMISSIONS.md §4a.5): Wenn das Projekt-Doc keinen Bearbeiter hat, wird
	 * der Owner automatisch eingetragen. Ein einmaliges Hinweis-Modal informiert
	 * den User darüber.
	 *
	 * - `bearbeiter[]` schon befüllt → no-op.
	 * - Owner fehlt zusätzlich → Fehler-Meldung, currentProject bleibt ungeladen.
	 * - Sonst: Hinweis-Modal mit „OK" + Owner in `bearbeiter[]` aufnehmen + speichern.
	 *
	 * Wird im Override von `loadProject` nach `super.loadProject` aufgerufen.
	 */
	override async loadProject(projectName: string, opts?: { transient?: boolean }): Promise<void> {
		await super.loadProject(projectName, opts);
		// §4a.5-Migration nur beim REGULÄREN Laden: Ein transienter Kontext
		// (Punkt-Detail-Resolver, Bug 2) darf keine Projekt-Mutation und kein
		// Migrations-Modal auslösen — der User hat das Projekt nicht geöffnet.
		if (!opts?.transient) await this.ensureBearbeiterMember();
	}

	private async ensureBearbeiterMember(): Promise<void> {
		if (this.bearbeiterMigrationRunning) return; // Schutz gegen Rekursion via save→loadProject
		const doc = untracked(() => this.currentProjectDoc());
		if (!doc) return;
		const bearbeiter = (doc as any).bearbeiter ?? [];
		if (Array.isArray(bearbeiter) && bearbeiter.length > 0) return;

		const owner = (doc as any).owner;
		if (!owner?.id) {
			await this._confirmService.error(
				'Dieses Projekt hat keinen Eigentümer und keine Bearbeiter — Datensanierung erforderlich. Das Projekt wird nicht geladen.',
				'Projekt nicht ladbar'
			);
			await this.unloadCurrentProject();
			return;
		}

		this.bearbeiterMigrationRunning = true;
		try {
			const ownerName = owner.name ?? owner.username ?? owner.id;
			await this._confirmService.alert(
				`Dieses Projekt hat keinen Bearbeiter. Der Owner (${ownerName}) wird automatisch als Bearbeiter eingetragen, damit Punkte angelegt und bearbeitet werden können.`,
				'Projekt-Migration: Bearbeiter ergänzen'
			);
			// Frischen Stand laden, damit kein 409 bei parallelem Edit auftritt.
			const fresh = await this.getOne(doc._id) as any;
			if (!fresh) return;
			fresh.bearbeiter = [{ id: owner.id, name: ownerName, username: owner.username }];
			await this.save(fresh);
			// currentProjectDoc explizit aktualisieren — ohne erneuten loadProject-Call (Rekursion).
			this.currentProjectDoc.set(fresh);
		} catch (err) {
			console.error('[ProjectService] Bearbeiter-Migration fehlgeschlagen:', err);
		} finally {
			this.bearbeiterMigrationRunning = false;
		}
	}
}
